// pages/FormPage.jsx - Mobile-First Pesticide Application Form
import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  Loader2, AlertCircle, Check, WifiOff
} from 'lucide-react'
import * as api from '../services/api'
import MethodSelector from '../components/MethodSelector'
import ProductSelector from '../components/ProductSelector'
import FormFields from '../components/FormFields'
import CompactWeatherDisplay from '../components/CompactWeatherDisplay'
import ProductAmountsSection from '../components/ProductAmountsSection'
import PhotoCapture from '../components/PhotoCapture'
import QuickAddProductModal from '../components/QuickAddProductModal'
import QuickAddEquipmentModal from '../components/QuickAddEquipmentModal'
import SubmissionSuccess from '../components/SubmissionSuccess'
import TodaysSubmissions from '../components/TodaysSubmissions'

export default function FormPage() {
  const { shortCode } = useParams()
  
  // Loading & error states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  
  // Form data from backend
  const [formLink, setFormLink] = useState(null)
  const [products, setProducts] = useState([])
  const [applicators, setApplicators] = useState([])
  const [equipment, setEquipment] = useState([])
  const [applicationMethods, setApplicationMethods] = useState([])
  const [todaysSubmissions, setTodaysSubmissions] = useState([])
  
  // Weather
  const [weather, setWeather] = useState(null)
  const [weatherWarnings, setWeatherWarnings] = useState([])
  const [loadingWeather, setLoadingWeather] = useState(false)
  
  // Form state
  const [formData, setFormData] = useState({
    applicationMethod: '',

    // Multi-product support
    selectedProducts: [],      // Array of product names
    productAmounts: {},        // Object: { productName: { amount, unit } }

    // Application-wide carrier (not per-product)
    carrierType: 'water',
    carrierAmount: '',
    carrierUnit: 'gal',

    // Customer info (editable from header)
    customerAddress: '',

    // Shared fields
    areaOrPlantTreated: '',
    equipment: '',
    applicatorName: '',
    applicatorLicense: '',
    supplier: '',
    temperature: '',
    humidity: '',
    windSpeed: '',
    windDirection: '',
    date: new Date().toLocaleDateString('en-US'),
    time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  })
  const [photos, setPhotos] = useState([])

  // UI state for inline editing
  const [weatherEditing, setWeatherEditing] = useState(false)

  // Modals
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [showAddEquipment, setShowAddEquipment] = useState(false)
  
  // Offline status
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  
  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])
  
  // Load form data
  useEffect(() => {
    loadFormData()
  }, [shortCode])
  
  const loadFormData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const data = await api.getFormData(shortCode)
      
      setFormLink(data.formLink)
      setProducts(data.products || [])
      setApplicators(data.applicators || [])
      setEquipment(data.equipment || [])
      setApplicationMethods(data.applicationMethods || [])
      setTodaysSubmissions(data.todaysSubmissions || [])

      // Initialize customer address from form link
      if (data.formLink?.jobAddress) {
        setFormData(prev => ({
          ...prev,
          customerAddress: data.formLink.jobAddress
        }))
      }

      // Set default applicator if only one
      if (data.applicators?.length === 1) {
        setFormData(prev => ({
          ...prev,
          applicatorName: data.applicators[0].name,
          applicatorLicense: data.applicators[0].licenseNumber
        }))
      }
      
    } catch (err) {
      console.error('Failed to load form:', err)
      setError(err.message || 'Failed to load form')
    } finally {
      setLoading(false)
    }
  }
  
  // Load weather
  const loadWeather = useCallback(async () => {
    if (!formLink) return

    setLoadingWeather(true)

    try {
      // Use job address from Jobber to fetch weather
      const result = await api.getWeather(shortCode, null, null, formLink.jobAddress)

      setWeather(result.weather)
      setWeatherWarnings(result.warnings || [])

      // Auto-fill weather fields
      if (result.weather && !result.weather.isDefault) {
        setFormData(prev => ({
          ...prev,
          temperature: result.weather.temperature?.toString() || prev.temperature,
          humidity: result.weather.humidity?.toString() || prev.humidity,
          windSpeed: result.weather.windSpeed?.toString() || prev.windSpeed,
          windDirection: result.weather.windDirection || prev.windDirection
        }))
      }

    } catch (err) {
      console.error('Failed to load weather:', err)
    } finally {
      setLoadingWeather(false)
    }
  }, [shortCode, formLink])

  // Load weather on mount (after formLink is loaded)
  useEffect(() => {
    if (formLink && !weather) {
      loadWeather()
    }
  }, [formLink, weather, loadWeather])
  
  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }
  
  // Select application method
  const selectMethod = (methodId) => {
    updateField('applicationMethod', methodId)
    // Clear selected products when method changes (favorites might differ)
    updateField('selectedProducts', [])
    updateField('productAmounts', {})
  }

  // Add product to selection (multi-product)
  const addProduct = (productName) => {
    if (!formData.selectedProducts.includes(productName)) {
      updateField('selectedProducts', [...formData.selectedProducts, productName])

      // Find the product to get its default unit
      const product = products.find(p => p.name === productName)
      const defaultUnit = product?.defaultUnit || 'oz'

      // Initialize product amount entry
      setFormData(prev => ({
        ...prev,
        productAmounts: {
          ...prev.productAmounts,
          [productName]: {
            amount: '',
            unit: defaultUnit
          }
        }
      }))
    }
  }

  // Remove product from selection
  const removeProduct = (productName) => {
    updateField('selectedProducts', formData.selectedProducts.filter(p => p !== productName))

    const newAmounts = { ...formData.productAmounts }
    delete newAmounts[productName]
    updateField('productAmounts', newAmounts)
  }

  // Update product amount
  const updateProductAmount = (productName, field, value) => {
    setFormData(prev => ({
      ...prev,
      productAmounts: {
        ...prev.productAmounts,
        [productName]: {
          ...prev.productAmounts[productName],
          [field]: value
        }
      }
    }))
  }
  
  // Select applicator
  const selectApplicator = (applicator) => {
    setFormData(prev => ({
      ...prev,
      applicatorName: applicator.name,
      applicatorLicense: applicator.licenseNumber
    }))
  }
  
  // Handle photo addition
  const handlePhotosChange = (newPhotos) => {
    setPhotos(newPhotos)
  }

  // Form validation (form-level, not step-based)
  const isFormValid = () => {
    // Method selected
    if (!formData.applicationMethod) return false

    // At least one product selected
    if (formData.selectedProducts.length === 0) return false

    // All products have amounts
    for (const productName of formData.selectedProducts) {
      const productData = formData.productAmounts[productName]
      if (!productData || !productData.amount) return false
    }

    // Carrier required check (application-wide)
    const currentMethod = applicationMethods.find(m => m.id === formData.applicationMethod)
    if (currentMethod?.requiresCarrier && !formData.carrierAmount) {
      return false
    }

    // Shared application details
    if (!formData.areaOrPlantTreated || !formData.applicatorName || !formData.equipment) {
      return false
    }

    // Weather is OPTIONAL - no validation

    return true
  }

  // Submit form (multi-product)
  const handleSubmit = async () => {
    if (submitting) return

    setSubmitting(true)

    try {
      // Build applications array (one per product)
      const applications = formData.selectedProducts.map(productName => {
        const productData = formData.productAmounts[productName]

        return {
          // Product info (unique per product)
          productName: productName,
          productAmount: productData.amount,
          productUnit: productData.unit,

          // Carrier (SAME for all products in this application)
          carrierType: formData.carrierType || '',
          carrierAmount: formData.carrierAmount || '',
          carrierUnit: formData.carrierUnit || '',

          // Customer info (editable in header)
          customerAddress: formData.customerAddress || '',

          // Shared application details (SAME for all products)
          applicationMethod: formData.applicationMethod,
          areaOrPlantTreated: formData.areaOrPlantTreated,
          equipment: formData.equipment,
          applicatorName: formData.applicatorName,
          applicatorLicense: formData.applicatorLicense,
          supplier: formData.supplier || '',
          date: formData.date,
          time: formData.time,

          // Weather (optional, SAME for all products)
          temperature: formData.temperature || '',
          humidity: formData.humidity || '',
          windSpeed: formData.windSpeed || '',
          windDirection: formData.windDirection || ''
        }
      })

      if (!isOnline) {
        api.savePendingSubmission(shortCode, { applications, photos })
        setSubmitted(true)
        return
      }

      await api.submitMultipleApplications(shortCode, applications, photos)
      setSubmitted(true)

    } catch (err) {
      console.error('Submission failed:', err)

      // Save applications array for retry
      const applications = formData.selectedProducts.map(productName => {
        const productData = formData.productAmounts[productName]
        return {
          productName: productName,
          productAmount: productData.amount,
          productUnit: productData.unit,
          carrierType: formData.carrierType || '',
          carrierAmount: formData.carrierAmount || '',
          carrierUnit: formData.carrierUnit || '',
          customerAddress: formData.customerAddress || '',
          applicationMethod: formData.applicationMethod,
          areaOrPlantTreated: formData.areaOrPlantTreated,
          equipment: formData.equipment,
          applicatorName: formData.applicatorName,
          applicatorLicense: formData.applicatorLicense,
          supplier: formData.supplier || '',
          date: formData.date,
          time: formData.time,
          temperature: formData.temperature || '',
          humidity: formData.humidity || '',
          windSpeed: formData.windSpeed || '',
          windDirection: formData.windDirection || ''
        }
      })
      api.savePendingSubmission(shortCode, { applications, photos })

      setError(`Submission failed: ${err.message}. Data saved locally for retry.`)
    } finally {
      setSubmitting(false)
    }
  }
  
  // Start new submission
  const startNewSubmission = () => {
    setSubmitted(false)
    setFormData(prev => ({
      ...prev,
      applicationMethod: '',
      selectedProducts: [],
      productAmounts: {},
      carrierType: 'water',
      carrierAmount: '',
      areaOrPlantTreated: '',
      date: new Date().toLocaleDateString('en-US'),
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    }))
    setPhotos([])
    loadFormData() // Refresh to get updated submissions
  }
  
  // Handle product added
  const handleProductAdded = (product) => {
    setProducts(prev => [...prev, product])
    addProduct(product.name)
    setShowAddProduct(false)
  }
  
  // Handle equipment added
  const handleEquipmentAdded = (equipmentItem) => {
    setEquipment(prev => [...prev, equipmentItem])
    updateField('equipment', equipmentItem.name)
    setShowAddEquipment(false)
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-arbor-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading form...</p>
        </div>
      </div>
    )
  }
  
  // Error state
  if (error && !formLink) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="card max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-xl font-semibold mt-4">Unable to Load Form</h2>
          <p className="text-gray-600 mt-2">{error}</p>
          <button 
            onClick={loadFormData}
            className="btn-primary mt-6"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }
  
  // Success state
  if (submitted) {
    return (
      <SubmissionSuccess 
        formLink={formLink}
        onNewSubmission={startNewSubmission}
        isOffline={!isOnline}
      />
    )
  }
  
  // Get current method details
  const currentMethod = applicationMethods.find(m => m.id === formData.applicationMethod)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-arbor-500 text-white sticky top-0 z-40">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="font-semibold">{formLink?.companyName || 'Pesticide Record'}</h1>
              <p className="text-sm text-arbor-100">
                Job #{formLink?.jobNumber} • {formLink?.clientName}
              </p>
            </div>
            {!isOnline && (
              <div className="flex items-center gap-1 text-yellow-200">
                <WifiOff className="w-4 h-4" />
                <span className="text-xs">Offline</span>
              </div>
            )}
          </div>

          {/* Editable Address */}
          <div className="mt-2">
            <input
              type="text"
              placeholder="Enter service address..."
              value={formData.customerAddress}
              onChange={(e) => updateField('customerAddress', e.target.value)}
              className="w-full px-3 py-1.5 text-sm bg-white/10 text-white placeholder-arbor-200
                         border border-white/20 rounded focus:outline-none focus:ring-2
                         focus:ring-white/30 focus:bg-white/15"
            />
          </div>
        </div>
      </header>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Single-page form */}
      <main className="pb-24">
        <div className="p-4 space-y-6">

          {/* 0. Today's Submissions - Show at TOP */}
          {todaysSubmissions.length > 0 && (
            <TodaysSubmissions submissions={todaysSubmissions} />
          )}

          {/* 1. Weather - Compact display */}
          <CompactWeatherDisplay
            weather={weather}
            warnings={weatherWarnings}
            formData={formData}
            updateField={updateField}
            loading={loadingWeather}
            onRefresh={loadWeather}
            editing={weatherEditing}
            onToggleEdit={() => setWeatherEditing(!weatherEditing)}
          />

          {/* 2. Application Method */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-3">
              Application Method <span className="text-red-500">*</span>
            </h3>
            <MethodSelector
              methods={applicationMethods}
              selectedMethod={formData.applicationMethod}
              onSelect={selectMethod}
            />
          </div>

          {/* 3. Product Selection - Show if method selected */}
          {formData.applicationMethod && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-3">
                Select Products <span className="text-red-500">*</span>
              </h3>
              <ProductSelector
                products={products}
                selectedProducts={formData.selectedProducts}
                method={formData.applicationMethod}
                onSelect={addProduct}
                onRemove={removeProduct}
                onAddNew={() => setShowAddProduct(true)}
                shortCode={shortCode}
                multiSelect={true}
              />
            </div>
          )}

          {/* 4. Application Details - Show if products selected */}
          {formData.selectedProducts.length > 0 && (
            <div className="card space-y-6">
              <h3 className="font-semibold text-gray-900">
                Application Details <span className="text-red-500">*</span>
              </h3>

              {/* Product Amounts Section */}
              <ProductAmountsSection
                selectedProducts={formData.selectedProducts}
                productAmounts={formData.productAmounts}
                updateProductAmount={updateProductAmount}
                removeProduct={removeProduct}
              />

              {/* Shared application fields */}
              <FormFields
                formData={formData}
                updateField={updateField}
                applicators={applicators}
                equipment={equipment}
                selectedApplicator={formData.applicatorName}
                onSelectApplicator={selectApplicator}
                requiresCarrier={currentMethod?.requiresCarrier}
                onAddEquipment={() => setShowAddEquipment(true)}
                hideProductFields={true}
              />
            </div>
          )}

          {/* 5. Photos - Show if products selected */}
          {formData.selectedProducts.length > 0 && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-3">
                Photos (Optional)
              </h3>
              <PhotoCapture
                photos={photos}
                onChange={handlePhotosChange}
              />
            </div>
          )}
        </div>
      </main>

      {/* Bottom submit button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 safe-area-inset-bottom">
        <button
          onClick={handleSubmit}
          disabled={submitting || !isFormValid()}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Check className="w-5 h-5" />
              Submit Application Record
            </>
          )}
        </button>
      </div>

      {/* Modals */}
      {showAddProduct && (
        <QuickAddProductModal
          shortCode={shortCode}
          onAdd={handleProductAdded}
          onClose={() => setShowAddProduct(false)}
        />
      )}

      {showAddEquipment && (
        <QuickAddEquipmentModal
          shortCode={shortCode}
          onAdd={handleEquipmentAdded}
          onClose={() => setShowAddEquipment(false)}
        />
      )}
    </div>
  )
}
